import React, { useState, useContext, useEffect } from 'react';
import { ICON_SIZE, SignInIcon } from "./actionIcons";
import fields from './fields';
import { GlobalContext } from "./GlobalContext";
import { setToken } from './csrf.js';
import { apiFetch } from './api.js';
import { Divider, TextInput } from '@tremor/react';
import { Fingerprint } from 'lucide-react';
import { browserSupportsWebAuthn, startAuthentication } from '@simplewebauthn/browser';
import { BrowserRouter, useNavigate } from 'react-router-dom';
import FullScreenScroll from './FullScreenScroll';


/**
 * The full-screen frame both states of this component sit in, lockup and all.
 *
 * WHY IT OWNS A SCROLLBAR. `body { overflow: hidden }` (carstyles.css:13) is
 * global, so there is no document scroll to fall back on: a full-screen box
 * here either scrolls itself or its overflow is unreachable. Before
 * `overflow-y-auto`, the sign-in card was 697px against a 660px window on an
 * ordinary 768px laptop panel, and `justify-center` splits the excess across
 * BOTH edges — the logo was cut off at the top at the same moment "Reset
 * password" went off the bottom, with no way to reach either. The passkey
 * button is what tipped it over (it and its divider are exactly 100px), but the
 * card has been one row away from this the whole time, which is the reason the
 * fix is a scroll container and not a shorter card.
 *
 * The inner box is `min-h-full`, NOT `min-h-screen`. It has to be free to grow
 * past the window for the scroll container to have anything to scroll, and
 * 100vh is the wrong number twice: it ignores this box's actual height, and on
 * a phone it counts a URL bar that is currently on screen.
 *
 * `short:` (max-height: 720px, tailwind.config.js) trims the padding and the
 * lockup so the two common laptop panels still fit outright — scrolling to sign
 * in is reachable, but it should stay the short-window case rather than the
 * everyday one.
 */
function AuthScreen({ title, subtitle, children }) {
    return (
      <FullScreenScroll>
          <div className="sm:mx-auto sm:w-full sm:max-w-md">
            {/* Centred as a unit. The lockup already reads "AUTODEALER UG", so
                it stands in for the welcome line that used to sit here. */}
            <div className="text-center">
              <img
                src="/logo-full.svg"
                alt="Auto Dealer UG"
                width="1600"
                height="1486"
                className="mx-auto w-52 short:w-36 h-auto"
              />
              <h3 className="mt-6 short:mt-3 text-tremor-title font-semibold text-tremor-content-strong dark:text-dark-tremor-content-strong">
                {title}
              </h3>
              <p className="mt-2 text-tremor-default text-tremor-content dark:text-dark-tremor-content">
                {subtitle}
              </p>
            </div>
            {children}
          </div>
      </FullScreenScroll>
    );
}

function LogIn({ setLogInStatus, setUserData }){
    const navigate = useNavigate();
    const { toggleMessageBox } = useContext(GlobalContext);
    const [creds, setCreds] = useState({
        EmailAddress: '',
        Password: ''
    });
    const [errors, setErrors] = useState({
        EmailAddress: '',
        Password: ''
    });
    const [touchedFields, setTouchedFields] = useState({});
    //Off by default, deliberately: a long-lived session is a choice the user
    //makes, not one made for them on a phone that may be shared.
    const [rememberMe, setRememberMe] = useState(false);
    const [passkeySupported, setPasskeySupported] = useState(false);
    const [passkeyBusy, setPasskeyBusy] = useState(false);
    const [passkeyError, setPasskeyError] = useState('');
    //Set when the credential belongs to more than one dealership. The password
    //(or passkey) has already been accepted at this point — what is missing is
    //which of them was meant. Null for everyone with a single membership, which
    //is almost everyone, and that path is untouched.
    const [dealershipChoices, setDealershipChoices] = useState(null);
    const [choosing, setChoosing] = useState(false);

    useEffect(() => {
        setPasskeySupported(browserSupportsWebAuthn());
    }, []);

    const handleChange = ({ target }) => {
        setCreds(prev => ({
            ...prev, [target.name]: target.value
        }));
        setTouchedFields(prev => ({ ...prev, [target.name]: true }));
    };

    const validateSingleInput = (name, value) => {
        let errorMessage = "";
        
        // Check if the field is empty
        if (!value) {
            errorMessage = `${fields[name].displayName || name} can't be empty`;
        } else {
            // Validate with regex if it's the EmailAddress field
            if (name === "EmailAddress") {
                const regex = new RegExp(fields.EmailAddress.regex);
                if (!regex.test(value)) {
                    errorMessage = "Invalid Email Address";
                }
            }
        }
        
        return errorMessage;
    };

    const validateAllInputs = () => {
        const errors = {
            EmailAddress: validateSingleInput("EmailAddress", creds.EmailAddress),
            Password: validateSingleInput("Password", creds.Password)
        };
        
        setErrors(errors);
        return !errors.EmailAddress && !errors.Password;
    };

    //Everything that has to happen on a successful sign-in, in one place.
    //Both the password and the passkey path end here — duplicating it would
    //mean duplicating the Preferences JSON.parse loop, which is exactly the
    //sort of thing that ends up subtly different in one of the two copies.
    const handleLoginSuccess = (resObj) => {
        setToken(resObj.csrfToken);
        setLogInStatus(true);
        navigate("/", { replace: true });
        //Preferences arrive as a map of JSON strings, one per sheet.
        const prefs = resObj.userData.Preferences || {};
        for (let sheetname of Object.keys(prefs)) {
            prefs[sheetname] = JSON.parse(prefs[sheetname]);
        }
        resObj.userData.Preferences = prefs;
        setUserData(resObj.userData);
    };

    //Both sign-in paths funnel through here so neither has to know whether a
    //second step is coming. `needsDealership` means the credential was accepted
    //but belongs to several dealerships; the session is deliberately NOT signed
    //in yet, so there is nothing to navigate to until one is picked.
    const handleAuthResponse = (resObj) => {
        if (resObj.needsDealership) {
            setToken(resObj.csrfToken);
            setDealershipChoices(resObj.choices || []);
            return;
        }
        handleLoginSuccess(resObj);
    };

    const chooseDealership = async (UserID) => {
        setChoosing(true);
        try {
            const res = await apiFetch('/login/select-dealership', { body: { UserID } });
            const resObj = await res.json();
            if (!res.ok || resObj.ok !== true) {
                toggleMessageBox({
                    on: true,
                    title: "Unable to continue",
                    message: resObj?.message || 'That dealership could not be opened.',
                    buttons: ["OK"],
                    clicked: ""
                });
                //Back to the form: the pending sign-in on the server is only
                //useful while this list is on screen, and leaving a dead list up
                //invites the same click again.
                if (res.status === 401) setDealershipChoices(null);
                return;
            }
            handleLoginSuccess(resObj);
        } catch (err) {
            toggleMessageBox({
                on: true,
                title: "Unable to continue",
                message: 'Unable to connect to the server: Please check your internet and try again.',
                buttons: ["OK"],
                clicked: ""
            });
        } finally {
            setChoosing(false);
        }
    };

    const handlePasskeyLogin = async () => {
        setPasskeyError('');
        setPasskeyBusy(true);
        try {
            const optRes = await apiFetch('/passkey/auth/options', { body: {} });
            const optObj = await optRes.json();
            if (!optRes.ok || !optObj.ok) {
                setPasskeyError(optObj?.message || 'Passkey sign-in is unavailable.');
                return;
            }

            //Opens the OS prompt. No email was typed and none is needed: the
            //credentials are discoverable, so the authenticator already knows
            //which accounts it holds for this site and offers them itself.
            const assertion = await startAuthentication({ optionsJSON: optObj.options });

            const verRes = await apiFetch('/passkey/auth/verify', {
                body: { response: assertion, RememberMe: rememberMe },
            });
            const resObj = await verRes.json();
            if (!verRes.ok || resObj.ok !== true) {
                setPasskeyError(resObj?.message || 'That passkey could not be verified.');
                return;
            }
            handleAuthResponse(resObj);
        } catch (err) {
            //NotAllowedError covers BOTH "the user cancelled" and "the prompt
            //timed out" — the spec refuses to distinguish them, precisely so a
            //site cannot work out whether someone has a passkey at all. Since
            //we cannot know it was not a deliberate cancel, saying anything
            //would be noise on the far more likely reading.
            if (err?.name === 'NotAllowedError' || err?.name === 'AbortError') return;
            //A get() is already outstanding (the autofill path). Not an error.
            if (err?.name === 'InvalidStateError') return;
            setPasskeyError('Passkey sign-in failed. Use your password instead.');
        } finally {
            setPasskeyBusy(false);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
            // Set all fields as touched for final validation on submit
        setTouchedFields({
          EmailAddress: true,
          Password: true
        });

        if (!validateAllInputs()) {
            console.log("Validation failed on submit.");
            return;
        }

        // Submit. If Ok, open CarsNav
        apiFetch('/login', { body: { ...creds, RememberMe: rememberMe } }).then(async (response)=>{
            const resObj = await response.json();
            console.log(`Cookie in login: ${document.cookie}`);
            console.log(`Cookie in header: ${response.headers.get("Set-Cookie")}`);
            if(!response.ok){
                if(response.status === 401){ //Wrong emailaddress or password
                    setErrors({
                      EmailAddress: resObj?.message || "emailaddress or password is wrong",
                      Password: resObj?.message || "emailaddress or password is wrong"
                    });
                    return;
                }
                //Some kind of db error
                toggleMessageBox({
                    on: true,
                    title: "Database error! Unable to login",
                    message: "Server error: "+resObj.message,
                    buttons: ["OK"],
                    clicked: ""
                });
            } else {
                if(resObj.ok!==true){ //Wrong emailaddress or password (legacy 200 shape)
                    setErrors({
                      EmailAddress: resObj?.message || "emailaddress or password is wrong",
                      Password: resObj?.message || "emailaddress or password is wrong"
                    });
                    return;
                } else { //Success — or a credential that spans several dealerships
                    handleAuthResponse(resObj);
                }
            }
        }).catch((err)=>{
            //Network error or something
            console.log(err);
            toggleMessageBox({
                on: true,
                title: "Unable To Login",
                message: "Unable to connect to the server: Please check your internet and try again.",
                buttons: ["OK"],
                clicked: ""
            });
        });
    };

    const handleBlur = ({ target }) => {
        const { name, value } = target;
        const errorMessage = validateSingleInput(name, value);
        setErrors(prev => ({ ...prev, [name]: errorMessage }));
    };

    //The credential was accepted; only "which dealership" is left. Rendered
    //instead of the form rather than over it, so there is no password box on
    //screen inviting someone to type it again — it has already been checked, and
    //asking twice would suggest the first attempt had failed.
    if (dealershipChoices) {
      return (
        <AuthScreen title="Choose a dealership" subtitle="Your account works at more than one.">
            {/* A dealership per row and no cap on how many: this is the one
                state whose height is set by the data rather than the design,
                so it is the state that most needs the frame to scroll. */}
            <div className="mt-8 flex flex-col gap-2">
              {dealershipChoices.map((c) => (
                <button
                  key={c.UserID}
                  type="button"
                  disabled={choosing}
                  onClick={() => chooseDealership(c.UserID)}
                  className="btn-t flex w-full items-center gap-3 px-4 py-3 text-left disabled:opacity-60"
                >
                  {c.LogoURL
                    ? <img src={c.LogoURL} alt="" className="h-8 w-8 shrink-0 rounded object-contain" />
                    : <span className="h-8 w-8 shrink-0 rounded bg-gray-200" />}
                  <span className="truncate font-medium">{c.CompanyName || 'Unnamed dealership'}</span>
                </button>
              ))}
            </div>

            <button
              type="button"
              className="link-t mt-6 w-full text-center underline"
              onClick={() => setDealershipChoices(null)}
              disabled={choosing}
            >
              Sign in as someone else
            </button>
        </AuthScreen>
      );
    }

    return (
      <AuthScreen
        title="Sign in to your account"
        subtitle={<>
          Don't have an account?{' '}
          <button
            className="link-t underline"
            onClick={() => {
              console.log("Coming soon!");
            navigate("/request-invite");
            }}
          >
            Request an invite
          </button>
        </>}
      >
        <form action="#" method="post" className="mt-6 space-y-4">
        <div>
          <label
          htmlFor="EmailAddress"
          className="text-tremor-default font-medium text-tremor-content-strong dark:text-dark-tremor-content-strong"
          >
          Email Address
          {errors['EmailAddress'] && <p className='input-errors'>{errors['EmailAddress']}</p>}
          </label>
          <TextInput
          type="email"
          id="EmailAddress"
          name="EmailAddress"
          placeholder="sarahconnor21@sky.net"
          className="mt-2"
          value={creds.EmailAddress} 
          onChange={handleChange} 
          onBlur={handleBlur}
          />
        </div>
        <div>
          <label
          htmlFor="Password"
          className="text-tremor-default font-medium text-tremor-content-strong dark:text-dark-tremor-content-strong"
          >
          Password
          {errors['Password'] && <p className='input-errors'>{errors['Password']}</p>}
          </label>
          <TextInput
          type="password"
          id="Password"
          name="Password"
          autoComplete="password"
          placeholder="Password"
          className="mt-2"
          value={creds.Password} 
          onChange={handleChange} 
          onBlur={handleBlur}
          />
        </div>
        {/* Wording avoids naming a duration on purpose — not because the 30
            days can't be honoured (they can, on iOS too: app. and api. are the
            same registrable domain, so ITP treats the cookie as first-party),
            but because a number in the copy is a promise, and a user who clears
            their cookies has broken it for us. */}
        <label className="mt-4 flex items-center gap-2 cursor-pointer text-tremor-default text-tremor-content dark:text-dark-tremor-content">
          <input
          type="checkbox"
          id="RememberMe"
          name="RememberMe"
          className="size-4 accent-[var(--accent-9)] cursor-pointer"
          checked={rememberMe}
          onChange={(e) => setRememberMe(e.target.checked)}
          />
          Keep me signed in on this device
        </label>
        <button
          className="btn-t btn-t-primary mt-4 w-full"
          onClick={handleSubmit}
        ><SignInIcon size={ICON_SIZE} />
          Sign in
        </button>
        </form>

        {/* The explicit button is the primary affordance, not conditional UI
            (passkeys offered inside the email field's autofill dropdown):
            autofill is invisible until focused, carries no label, and is
            unevenly implemented — a poor thing to make the only route in. */}
        {passkeySupported && (
          <>
          {/* my-6 → my-3 on a short window. The divider and this button are
              exactly 100px together, which is what turned a card that just fit
              a 768px laptop panel into one that no longer does. */}
          <Divider className="my-6 short:my-3">or</Divider>
          {passkeyError && <p className="input-errors mb-2 text-center">{passkeyError}</p>}
          <button
            type="button"
            className="btn-t btn-t-secondary w-full flex items-center justify-center gap-2"
            onClick={handlePasskeyLogin}
            disabled={passkeyBusy}
          >
            <Fingerprint className="size-4" aria-hidden="true" />
            {passkeyBusy ? 'Waiting for your device…' : 'Sign in with a passkey'}
          </button>
          </>
        )}
        <p className="mt-6 short:mt-3 text-tremor-default text-tremor-content dark:text-dark-tremor-content">
        Forgot your password?{' '}
        <button
          className="link-t underline"
          onClick={() => {
          navigate("/forgotpassword");
          }}
        >
          Reset password
        </button>
        </p>
      </AuthScreen>
    );
}

export default LogIn;